import { Component, inject } from '@angular/core';
import { CommonModule, PlatformLocation } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ActionItemComponent } from '../shared/action-item.component';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { ToastrService } from 'ngx-toastr';
import { InvitesService } from './invites.service';
import { BehaviorSubject, map } from 'rxjs';
import { SchoolsService } from '../schools/schools.service';

@Component({
  selector: 'oc-edit-invite',
  standalone: true,
  imports: [CommonModule, RouterModule, ActionItemComponent, ReactiveFormsModule, FormlyBootstrapModule, FormlyModule],
  template: `
    <div class="modal-container">
      <div class="modal-spacer"></div>
      <div class="modal-body">
        <h1 *ngIf="!id">Invite</h1>
        <h1 *ngIf="id">Edit Invite</h1>
        <form [formGroup]="form" (ngSubmit)="onSubmit(model)">
          <formly-form [form]="form" [fields]="fields" [model]="model" [options]="options"></formly-form>
          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary mt-3 mb-4">
              <span *ngIf="saving" class="spinner-border spinner-border-sm"></span>
              <span *ngIf="!saving">Save Changes</span>
            </button>
            <button type="button" class="btn btn-secondary mt-3 mb-4" (click)="activeModal.dismiss('Cancel')">
              Cancel
            </button>
          </div>
        </form>
      </div>
      <div class="modal-spacer">
        <oc-action-item icon="tablerX" size="lg" (click)="activeModal.dismiss('Cross click')"></oc-action-item>
      </div>
    </div>
  `,
  styles: `
    .modal-container {
      display: flex;
      min-height: 100vh;
    }

    .modal-body {
      flex: 0;
      flex-basis: 560px;
      padding: 2.5rem 3.5rem;
    }

    .modal-spacer {
      flex: 1; 
    }

    .modal-spacer:last-child {
      padding: 2rem;
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
    }

    h1 {
      margin-bottom: 2rem;
    }
  `,
})
export class EditInviteComponent {
  private platformLocation = inject(PlatformLocation);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private invitesService = inject(InvitesService);
  private schoolsService = inject(SchoolsService);
  public activeModal = inject(NgbActiveModal);

  public id: string | null = null;

  public schoolId$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public schoolId: string | null = null;

  saving = false;
  options: FormlyFormOptions = {
    formState: {
      submitted: false,
    },
  };

  form = new FormGroup({});
  model = { email: '', role: null };
  fields: FormlyFieldConfig[] = [
    {
      key: 'email',
      type: 'input',
      props: {
        label: 'Email',
        required: true,
        type: 'email',
      },
    },
    {
      key: 'role',
      type: 'select',
      props: {
        label: 'Role',
        required: true,
        options: [
          { label: 'Choose role...', value: null },
          { label: 'Admin', value: 0 },
          { label: 'Teacher', value: 1 },
        ],
      },
    },
    {
      key: 'schoolId',
      type: 'typeahead',
      expressions: {
        hide: this.schoolId$.pipe(map((schoolId) => schoolId != null)),
      },
      props: {
        label: 'School',
        required: true,
        placeholder: 'Search',
        search$: (term: string) => {
          return this.schoolsService.getSchools().pipe(
            map((schools) =>
              schools.filter((school) => school.name.toLowerCase().indexOf(term.toLowerCase()) !== -1 || !term),
            ),
            map((schools) => schools.map((school) => ({ value: school.id, label: school.name }))),
          );
        },
      },
    },
  ];

  constructor() {
    this.platformLocation.onPopState(() => this.modalService.dismissAll());
  }

  onSubmit(model: any) {
    this.options.formState.submitted = true;
    if (this.saving) {
      return;
    }
    if (!this.form.valid) {
      this.toastr.error('Please review and try again');
      return;
    }

    this.saving = true;

    const data = {
      ...model,
      schoolId: this.schoolId ?? model.schoolId.value,
    };

    if (this.id) {
      this.invitesService.updateInvite(this.id, data).subscribe((response) => {
        this.toastr.success('Invite updated');
        this.activeModal.close(response);
      });
    } else {
      this.invitesService.createInvite(data).subscribe((response) => {
        this.toastr.success('Invite created');
        this.activeModal.close(response);
      });
    }
  }
}
